import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useUser } from 'hooks/useUser';
import { getStorageValue, setStorageValue, removeStorageValue } from '@portal-internet/core';

// ✅ Variable global per inhabilitar Adobe Target
const ADOBE_TARGET_ENABLED = false; // Canvia a false per desactivar

const GlobalAdobeTargetContext = createContext({
    getVariant: () => "Z",
    getVariantSync: () => "Z",
    isTargetReady: false,
    isUserReady: false,
    variants: new Map()
});

export const GlobalAdobeTargetProvider = ({ children }) => {
    const [variants, setVariants] = useState(new Map());
    const [isTargetReady, setIsTargetReady] = useState(false);
    const { isAuthenticated, uuid } = useUser();

    const ADOBE_TARGET_PRE_TOKEN =  "c476b23d-2117-de96-a88e-b4cdf710a0a7",
            ADOBE_TARGET_PRO_TOKEN =  "072656b5-6716-1c5f-6122-3aeabb7fc80f";

    // ✅ Configuració simple
    const MBOX_STRATEGIES = {
        'home-tira-contingut-adicional': { storageDays: 14 },
        'tira_segments_edat': { storageDays: 1 },
        'home_tira_aniv': { storageDays: 1 }
    };

    // ✅ Verificar localStorage
    const isStorageAvailable = () => {
        return !!(typeof window !== 'undefined' && window?.CCMAStorage);
    };

    // ✅ Guardar variant
    const saveVariant = (mboxName, variant) => {
        if (!isStorageAvailable() || !uuid) return;

        const storageKey = `adobeTarget_${mboxName}`;
        const storageDays = MBOX_STRATEGIES[mboxName].storageDays;

        // ✅ Càlcul manual en milisegons
        //const millisecondsPerDay = 24 * 60 * 60 * 1000; // 86400000 ms
        //const expirationTimestamp = Date.now() + (storageDays * millisecondsPerDay);
        //const expirationDate = new Date(expirationTimestamp);


        try {
            setStorageValue(storageKey, variant, {
                path: '/',
                domain: '.3cat.cat',
                expires: storageDays,
                naturalDays: true
            });
        } catch (error) {
            console.warn(`Error guardant ${mboxName}:`, error);
        }
    };

    // ✅ Llegir variant
    const loadVariant = async (mboxName) => {
        if (!isStorageAvailable() || !uuid) return null;

        const storageKey = `adobeTarget_${mboxName}`;
        const variant = await getStorageValue(storageKey);

        // ✅ getStorageValue ja gestiona l'expiració automàticament
        // Si retorna null, és que ha expirat o no existeix
        if (variant) {
            return variant;
        }

        return null; // Ha expirat
    };

    // ✅ Obtenir cookie AMCV
    const getAMCVCookie = () => {
        if (typeof window === 'undefined') return null;

        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name && name.startsWith('AMCV_')) {
                try {
                    const decodedValue = decodeURIComponent(value);
                    const parts = decodedValue.split('|');
                    for (let i = 0; i < parts.length; i++) {
                        if (parts[i] === 'MCMID' && parts[i + 1]) {
                            return parts[i + 1];
                        }
                    }
                    return parts[3] || null;
                } catch (error) {
                    return null;
                }
            }
        }
        return null;
    };

    // ✅ Crida SIMPLE a Adobe Target
    const callAdobeTarget = async (mboxNames) => {

        if (!ADOBE_TARGET_ENABLED || !uuid || !mboxNames.length || !isAuthenticated) return {};

        const AMCVCookie = getAMCVCookie();
        if (!AMCVCookie) return {};

        try {
            const url = `https://cctigeneralitat.tt.omtrdc.net/rest/v1/delivery?client=cctigeneralitat&sessionId=${Date.now()}`;

            const body = {
                context: {
                    channel: "web",
                    address: { url: window?.location?.href || "https://3cat.cat" },
                    browser: { host: window?.location?.hostname || "3cat.cat" }
                },
                id: { marketingCloudVisitorId: AMCVCookie },
                property : {
                    token: 
                        window.jitConfig.entorn === 'aws_pro' ? ADOBE_TARGET_PRO_TOKEN :
                        ADOBE_TARGET_PRE_TOKEN
                },
                execute: {
                    mboxes: mboxNames.map((mboxName, index) => ({
                        name: mboxName,
                        index: index + 1,
                        parameters: {
                            "user.userId": uuid,
                            "user.type": "registered"
                        }
                    }))
                }
            };

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                return {};
            }

            const data = await response.json();
            const results = {};

            mboxNames.forEach(mboxName => {
                const mbox = data?.execute?.mboxes?.find(m => m.name === mboxName);
                const content = mbox?.options?.[0]?.content;

                let variant = 'Z';
                if (content) {
                    if (typeof content === "string") {
                        try {
                            const parsed = JSON.parse(content);
                            variant = parsed.version || parsed.variant || parsed.value || content;
                        } catch (e) {
                            variant = content;
                        }
                    } else if (typeof content === "object") {
                        variant = content.version || content.variant || content.value || 'Z';
                    }
                }

                results[mboxName] = variant;
            });

            return results;

        } catch (error) {
            return {};
        }
    };

    // ✅ Verificar Adobe Target
    useEffect(() => {
        const checkTarget = () => {
            if (typeof window !== 'undefined' && window.adobe && window.adobe.target) {
                setIsTargetReady(true);
                return true;
            }
            return false;
        };

        if (!checkTarget()) {
            const interval = setInterval(() => {
                if (checkTarget()) {
                    clearInterval(interval);
                }
            }, 100);

            setTimeout(() => clearInterval(interval), 15000);
            return () => clearInterval(interval);
        }
    }, []);

    const cleanupOldVariants = async () => {
        if (!isStorageAvailable()) return;
        try {
            // ✅ Claus vàlides que volem mantenir
            const validKeys = Object.keys(MBOX_STRATEGIES).map(mbox => `adobeTarget_${mbox}`);

            // ✅ Obtenir totes les claus de localStorage que comencin per "adobeTarget_"
            const adobeTargetKeys = [];

            if (typeof window !== 'undefined' && window.localStorage) {
                // Iterar per totes les claus de localStorage
                for (let i = 0; i < window.localStorage.length; i++) {
                    const key = window.localStorage.key(i);
                    if (key && key.startsWith('adobeTarget_')) {
                        adobeTargetKeys.push(key);
                    }
                }
            }

            // ✅ Identificar claus a eliminar (les que NO estan en MBOX_STRATEGIES)
            const keysToDelete = adobeTargetKeys.filter(key => !validKeys.includes(key));

            if (keysToDelete.length > 0) {
                // ✅ Eliminar variants no vàlides
                for (const keyToDelete of keysToDelete) {
                    try {
                        await removeStorageValue(keyToDelete);
                    } catch (error) {
                        console.warn(`❌ Error eliminant ${keyToDelete}:`, error);
                    }
                }

            }

        } catch (error) {
            console.warn('❌ Error durant la neteja de variants:', error);
        }
    }

    // ✅ Carregar/verificar variants
    useEffect(() => {
        if (!uuid || !isAuthenticated || !isTargetReady) return;

        const initVariants = async () => {

            await cleanupOldVariants();

            const loadedVariants = new Map();
            const missingMboxes = [];

            // 1. Intentar carregar del localStorage
            for (const mboxName of Object.keys(MBOX_STRATEGIES)) {
                const savedVariant = await loadVariant(mboxName);
                if (savedVariant) {
                    loadedVariants.set(mboxName, savedVariant);
                } else {
                    missingMboxes.push(mboxName);;
                }
            }

            // 2. Establir variants carregades
            if (loadedVariants.size > 0) {
                setVariants(loadedVariants);
            }

            // 3. Carregar variants que falten des d'Adobe Target
            if (missingMboxes.length > 0) {

                const results = await callAdobeTarget(missingMboxes);

                for (const [mboxName, variant] of Object.entries(results)) {
                    await saveVariant(mboxName, variant);
                    setVariants(prev => new Map(prev).set(mboxName, variant));
                }
            }
        };

        initVariants();
    }, [uuid, isAuthenticated, isTargetReady]);

    // ✅ Funcions públiques
    const getVariant = useMemo(() => async (mboxName, fallback = "A") => {
        // Si la tenim en memòria
        if (variants.has(mboxName)) {
            return variants.get(mboxName);
        }

        // Intentar carregar del localStorage
        const savedVariant = await loadVariant(mboxName);
        if (savedVariant) {
            setVariants(prev => new Map(prev).set(mboxName, savedVariant));
            return savedVariant;
        }

        // Carregar sota demanda des d'Adobe Target
        if (isTargetReady && isAuthenticated) {
            const results = await callAdobeTarget([mboxName]);
            const variant = results[mboxName] || fallback;

            await saveVariant(mboxName, variant);
            setVariants(prev => new Map(prev).set(mboxName, variant));

            return variant;
        }

        return fallback;
    }, [variants, isTargetReady, isAuthenticated, uuid]);

    const getVariantSync = useMemo(() => (mboxName, fallback = "A") => {
        return variants.get(mboxName) || fallback;
    }, [variants]);

    const contextValue = useMemo(() => ({
        getVariant,
        getVariantSync,
        isTargetReady,
        isUserReady: !!uuid,
        variants,
        isInitialized: true
    }), [getVariant, getVariantSync, isTargetReady, uuid, variants]);

    return (
        <GlobalAdobeTargetContext.Provider value={contextValue}>
            {children}
        </GlobalAdobeTargetContext.Provider>
    );
};

export const useGlobalAdobeTarget = () => {
    const context = useContext(GlobalAdobeTargetContext);
    if (!context) {
        throw new Error('useGlobalAdobeTarget must be used within a GlobalAdobeTargetProvider');
    }
    return context;
};
